import React, { useEffect, useState } from 'react';
import { EmailInputPage as PageType, UserProfile } from '../../types/quiz';
import { validateEmail } from '../../utils/quizLogic';
import { trackEvent } from '../../utils/trackEvent';
import { useLang } from '../../i18n/LangContext';
import { useT } from '../../i18n/useT';
import '../../assets/scss/quiz-editorial.scss';

/** Profile field the marketing opt-in is stored under, and the column it maps to. */
export const CONSENT_FIELD = 'marketing_consent';

interface Props {
    page: PageType;
    userProfile: UserProfile;
    onAnswer: (value: string, extra?: Record<string, any>) => void;
}

const EmailInputPage: React.FC<Props> = ({ page, userProfile, onAnswer }) => {
    const { lang } = useLang();
    const t = useT();
    const initialValue = page.data_field ? (userProfile[page.data_field] as string) || '' : '';
    const [email, setEmail] = useState<string>(initialValue);
    const [error, setError] = useState<string | null>(null);
    // Opted in by default, as specified. Note this is a pre-ticked box: under
    // GDPR that is not valid consent for EU recipients, who need an unticked
    // one. Flip the initial value here if that market comes into scope.
    const [consent, setConsent] = useState<boolean>(
        userProfile[CONSENT_FIELD] === undefined ? true : !!userProfile[CONSENT_FIELD],
    );

    useEffect(() => {
        setEmail(initialValue);
    }, [initialValue]);

    const handleSubmit = () => {
        // Validate
        if (!email.trim()) {
            setError(lang === 'uk' ? 'Введи email' : 'Email is required');
            return;
        }

        if (!validateEmail(email)) {
            setError(lang === 'uk' ? 'Введи коректну email-адресу' : 'Please enter a valid email address');
            return;
        }

        // Submit
        const normalizedEmail = email.trim();

        setError(null);
        // Funnel: the user gave a valid email. Persisted to the events table so
        // we can measure email_submitted -> checkout_initiated -> payment.
        trackEvent('email_submitted', { email: normalizedEmail, metadata: { [CONSENT_FIELD]: consent } });
        onAnswer(normalizedEmail, { [CONSENT_FIELD]: consent });
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSubmit();
        }
    };

    const handleBlur = () => {
        if (email.trim() && !validateEmail(email)) {
            setError(lang === 'uk' ? 'Введи коректну email-адресу' : 'Please enter a valid email address');
        }
    };

    return (
        <div className="page page--email-input">
            <div className="page__container">
                <h2 className="page__question">{page.question}</h2>
                {page.subtitle && <p className="page__subtitle">{page.subtitle}</p>}

                <div className="email-input">
                    <input
                        type="email"
                        name="email"
                        autoComplete="email"
                        inputMode="email"
                        className={`email-input__field ${error ? 'email-input__field--error' : ''}`}
                        placeholder={page.placeholder}
                        value={email}
                        onChange={(e) => {
                            setEmail(e.target.value);
                            if (error) setError(null);
                        }}
                        onBlur={handleBlur}
                        onKeyPress={handleKeyPress}
                        autoFocus
                    />
                    {error && <p className="email-input__error">{error}</p>}
                </div>

                <label className="consent" htmlFor={CONSENT_FIELD}>
                    <input
                        id={CONSENT_FIELD}
                        type="checkbox"
                        className="consent__box"
                        checked={consent}
                        onChange={(e) => setConsent(e.target.checked)}
                    />
                    <span className="consent__label">{t('quiz.email.consent')}</span>
                </label>

                {page.privacyNote && <p className="page__privacy-note">{page.privacyNote}</p>}

                <div className="page__actions">
                    <button type="button" className="page__button page__button--block" onClick={handleSubmit} disabled={!email.trim()}>
                        {page.buttonText}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EmailInputPage;
